<template>
	<section class="accommodation grid-container">
		<!-- Show price, button (add to cart) or (remove from cart) -->
		<div
			class="accommodation__sticky"
			v-if="
				!loading && selectedStartDate && selectedEndDate && accommodation.price
			"
		>
			<span class="accommodation__stickyprice">
				<p class="accommodation__stickydate small">
					{{ formatDateWithYear(selectedStartDate) }} -
					{{ formatDateWithYear(selectedEndDate) }}
				</p>
				<span class="h3">
					{{ convertPrice(accommodation.price) }} {{ accommodation.currency }}
				</span>
			</span>
			<!-- If on cart (show remove button) -->
			<Button
				v-if="isAccomodationInCart()"
				class="btn--red"
				@click.native="removeFromCart()"
				size="small"
			>
				{{ $t('accommodation.remove') }}
			</Button>
			<!-- If not in cart (show add button) -->
			<Button
				v-else
				class="btn--less-padding"
				@click.native="!loading ? addToCart() : () => {}"
				:loading="loading"
				size="small"
			>
				{{ $t('accommodation.add') }}
			</Button>
		</div>

		<div class="grid-x">
			<div class="cell medium-12">
				<button
					v-if="fromWhat()"
					@click.prevent="goBack"
					class="accommodation__back"
				>
					<svg width="27" height="12" viewBox="0 0 27 12" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'icon'" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path d="M.296 6l5.2-5.2.8.8-3.775 3.775h23.583v1.25H2.52L6.296 10.4l-.8.8L.296 6z" fill="#949EB1" fill-rule="evenodd"/></svg>
					<span>{{ $t('accommodation.back') }}</span>
				</button>
			</div>
			<div class="cell medium-12 large-9">
				<div class="accommodation__imageslider">
					<ImageSlider :images="mapImages()" :loading="loading" />
				</div>
				<div class="accommodation__info" v-if="!loading">
					<div
						v-if="selectedStartDate && selectedEndDate && accommodation.price"
						class="accommodation__book"
						:class="{ 'accommodation__book--discount': isDiscount }"
					>
						<button
							:class="[
								'accommodation__dates',
								isAccomodationInCart() == false
									? 'accommodation__dates--cursor'
									: 'accommodation__dates--disabled'
							]"
							@click="showDatepickerModal"
						>
							<div class="accommodation__dates__title">
								<svg data-name="Lager 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:class="'icon-calendar'" v-bind:tabindex="'-1'"><path d="M22 2H2a1 1 0 00-1 1v18a1 1 0 001 1h20a1 1 0 001-1V3a1 1 0 00-1-1zM6.75 20H3v-3h3.75zm0-4H3v-3h3.75zm0-4H3V9h3.75zm4.75 8H7.75v-3h3.75zm0-4H7.75v-3h3.75zm0-4H7.75V9h3.75zm4.75 8H12.5v-3h3.75zm0-4H12.5v-3h3.75zm0-4H12.5V9h3.75zM21 20h-3.75v-3H21zm0-4h-3.75v-3H21zm0-4h-3.75V9H21zm0-5H3V4h18z"/></svg>
								<h6>
									{{ formatDateWithYear(selectedStartDate) }} -
									{{ formatDateWithYear(selectedEndDate) }}
								</h6>
								<span v-if="isAccomodationInCart() == false">
									<svg data-name="Lager 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:class="'icon-pen'" v-bind:tabindex="'-1'"><path d="M20.45 7.13L17.63 4.3a1 1 0 00-1.42 0L5 15.5a.88.88 0 00-.27.51L4 19.54a1 1 0 00.27.9 1 1 0 00.71.3h.2L8.75 20a1.1 1.1 0 00.51-.27L20.45 8.55a1 1 0 00.3-.71 1 1 0 00-.3-.71zM6.29 18.43L6.58 17a2.37 2.37 0 01.65.49 2.42 2.42 0 01.49.65zm2.29-.84a3.68 3.68 0 00-.61-.8 3.94 3.94 0 00-.8-.61L15 8.37l1.41 1.41zM17.13 9l-1.44-1.34 1.23-1.24 1.41 1.42z"/></svg>
								</span>
							</div>
							<p v-if="true" class="small small--color-lynch selected-people">
								<span v-if="numberOfPersons.amountAdults > 0">
									{{ numberOfPersons.amountAdults }}
									<span v-if="numberOfPersons.amountAdults === 1">
										{{ $t('accommodation.adult') }}
									</span>
									<span v-else>{{ $t('accommodation.adults') }}</span>
								</span>
								<span v-if="numberOfPersons.amountYouths > 0">
									{{ numberOfPersons.amountYouths }}
									<span v-if="numberOfPersons.amountYouths === 1">
										{{ $t('accommodation.youth') }}
									</span>
									<span v-else>{{ $t('accommodation.youths') }}</span>
								</span>
								<span v-if="numberOfPersons.amountChildren > 0">
									{{ numberOfPersons.amountChildren }}
									<span v-if="numberOfPersons.amountChildren === 1">
										{{ $t('accommodation.child') }}
									</span>
									<span v-else>{{ $t('accommodation.children') }}</span>
								</span>
							</p>

							<!-- Mandatory addons -->
							<template v-if="mandatoryAddons().length > 0">
								<span class="accommodation__dates__labels-headline">
									{{ $t('accommodation.including') }}:
								</span>

								<ul class="accommodation__dates__labels">
									<template v-for="addon in mandatoryAddons()">
										<li :key="mandatoryAddons().indexOf(addon)">{{ addon }}</li>
									</template>
								</ul>
							</template>
						</button>

						<!-- Show price, button (add to cart) or (remove from cart) -->
						<div class="accommodation__book__actions">
							<span v-if="!isDiscount" class="accommodation__price">
								{{ convertPrice(accommodation.price) }}
								{{ accommodation.currency }}
							</span>
							<span v-if="isDiscount" class="accommodation__price">
								<div class="accommodation__discountLabelWrapper">
									{{ convertPrice(accommodation.price) }}
									{{ accommodation.currency }}
									<span class="accommodation__discontLabel">
										<span class="accommodation__discontLabel__value">
											-{{
												convertPrice(
													accommodation.ordinaryprice - accommodation.price
												)
											}}
											SEK
										</span>
									</span>
								</div>
								<span class="accommodation__price__full">
									Ord. pris {{ convertPrice(accommodation.ordinaryprice) }} SEK
								</span>
							</span>
							<!-- If on cart (show remove button) -->
							<Button
								v-if="isAccomodationInCart()"
								class="btn--red"
								@click.native="removeFromCart()"
							>
								{{ $t('accommodation.remove') }}
							</Button>
							<!-- If not in cart (show add button) -->
							<Button
								v-else
								class="btn--less-padding"
								@click.native="!loading ? addToCart() : () => {}"
								:loading="loading"
							>
								{{ $t('accommodation.add') }}
							</Button>
						</div>
					</div>
					<!-- check Availability button (Opens datepicker modal) -->
					<div v-else class="accommodation__book">
						<div class="accommodation__availability">
							<Button
								@click.native="showModal = true"
								:disabled="allowedStartDates.length ? false : true"
							>
								{{
									allowedStartDates.length
										? $t('accommodation.checkAvailability')
										: $t('accommodation.noDatesAvailable')
								}}
							</Button>
						</div>
					</div>
					<span class="accommodation__type">{{ accommodation.type }}</span>
					<div class="accommodation__head">
						<h1 class="accommodation__title">{{ accommodation.title }}</h1>
					</div>

					<!---********** Description **********-->
					<div class="accommodation__description">
						{{ accommodation.description }}
					</div>

					<!---********** Info icons **********-->
					<div
						v-if="!accommodation.type == 'Campingplats'"
						class="accommodation__sleeping-arrangements"
					>
						<Accommodationinfo
							:beds="accommodation.sleepingArrangementBeds"
							:bedrooms="accommodation.sleepingArrangementRooms"
							:area="accommodation.sleepingArrangementArea"
							:distanceToLift="accommodation.utilities.lift"
							:extraBeds="accommodation.numberOfExtraBeds"
							:loft="accommodation.loft"
						/>
					</div>

					<!---********** Facilities **********-->
					<div class="accommodation__facilities">
						<div class="accommodation__icon">
							<svg data-name="Lager 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path d="M16 16h-5a1 1 0 000 2h5a1 1 0 000-2zM8.38 12.07a1 1 0 00-1.09.22 1 1 0 000 1.42 1.15 1.15 0 00.33.21A1 1 0 009 13a1.05 1.05 0 00-.29-.71 1.46 1.46 0 00-.33-.22zM8.38 16.07a1 1 0 00-1.09.22 1 1 0 00-.21 1.09.9.9 0 00.54.54 1 1 0 001.09-.21 1.15 1.15 0 00.21-.33A1 1 0 009 17a1.05 1.05 0 00-.29-.71 1.46 1.46 0 00-.33-.22zM8.38 8.07a1 1 0 00-1.09.22 1 1 0 000 1.42 1.15 1.15 0 00.33.21A1 1 0 009 9a1.05 1.05 0 00-.29-.71 1.46 1.46 0 00-.33-.22z"/><path d="M18 2h-1V1a1 1 0 00-2 0v1h-2V1a1 1 0 00-2 0v1H9V1a1 1 0 00-2 0v1H6a3 3 0 00-3 3v15a3 3 0 003 3h12a3 3 0 003-3V5a3 3 0 00-3-3zm1 18a1 1 0 01-1 1H6a1 1 0 01-1-1V5a1 1 0 011-1h1v1a1 1 0 002 0V4h2v1a1 1 0 002 0V4h2v1a1 1 0 002 0V4h1a1 1 0 011 1z"/><path d="M16 12h-5a1 1 0 000 2h5a1 1 0 000-2zM16 8h-5a1 1 0 000 2h5a1 1 0 000-2z"/></svg>
						</div>
						<div class="accommodation__facilities-wrapper">
							<div class="accommodation__label">
								{{ $t('search.characteristicsAccordionSubtitle') }}
							</div>
							<ul class="accommodation__list">
								<li
									v-for="facility in [...accommodation.facilitiesData].sort()"
									:key="facility.id"
								>
									<svg width="13" height="12" viewBox="0 0 13 12" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><defs><path d="M5.429 11a1 1 0 01-.76-.348l-3.427-4A1 1 0 112.759 5.35l2.506 2.923 3.867-6.768a.997.997 0 011.364-.372c.48.274.647.885.372 1.364l-4.571 8a1.002 1.002 0 01-.868.504" id="a"/></defs><g transform="translate(.8)" fill="none" fill-rule="evenodd"><mask id="b" fill="#fff"><use xlink:href="#a"/></mask><use fill="#000" xlink:href="#a"/><g mask="url(#b)" fill="#7F8BA1"><path d="M0 0h12v12H0z"/></g></g></svg>
									<span>{{ facility }}</span>
								</li>
							</ul>
						</div>
					</div>

					<!---********** District **********-->
					<div class="accommodation__district">
						<div class="accommodation__icon">
							<svg data-name="Lager 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path d="M11.5 6.32a2.88 2.88 0 102.88 2.88 2.89 2.89 0 00-2.88-2.88zm0 4.6a1.73 1.73 0 111.73-1.72 1.73 1.73 0 01-1.73 1.72z"/><path d="M11.5 1.15A7.49 7.49 0 004 8.62c0 3.9 2.76 9.2 6.71 12.89l.79.73.79-.73c3.95-3.69 6.71-9 6.71-12.89a7.49 7.49 0 00-7.5-7.47zm0 17.9c-3.41-3.53-5.19-7.82-5.19-10.43a5.19 5.19 0 0110.38 0c0 2.61-1.78 6.9-5.19 10.43z"/></svg>
						</div>
						<div>
							<div class="accommodation__label">
								{{ $t('search.areaAccordionSubtitle') }}
							</div>
							<p v-if="accommodation.districtText" class="accommodation__text">
								{{ accommodation.districtText }}
							</p>
						</div>
					</div>

					<!---********** Addons **********-->
					<div
						v-if="accommodationAddon.length > 0"
						class="accommodation__addon-description"
					>
						<div class="accommodation__icon">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 20" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path class="st0" d="M17.9 10c-.6 0-1 .4-1 1v6h-12V5h6c.6 0 1-.4 1-1s-.4-1-1-1h-7c-.6 0-1 .4-1 1v14c0 .6.4 1 1 1h14c.6 0 1-.4 1-1v-7c0-.6-.5-1-1-1zM20.9 3h-2V1c0-.6-.4-1-1-1s-1 .4-1 1v2h-2c-.6 0-1 .4-1 1s.4 1 1 1h2v2c0 .6.4 1 1 1s1-.4 1-1V5h2c.6 0 1-.4 1-1s-.5-1-1-1z"/><path class="st0" d="M9.6 10.3c-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4l2 2c.2.2.4.3.7.3h.1c.3 0 .6-.2.7-.4l3-4c.3-.4.2-1.1-.2-1.4-.5-.3-1.1-.2-1.4.2l-2.3 3.1-1.2-1.2z"/></svg>
						</div>
						<div class="accommodation__addon-description-wrapper">
							<div class="accommodation__label">
								{{ $t('accommodation.addonListLabel') }}
							</div>
							<ul>
								<template v-for="addon in accommodationAddon">
									<li
										v-if="addon.mandatory_web === false"
										:key="addon.extraslid"
									>
										<h5>
											<span>
												<span class="bull">&bull;</span>
												{{ addon.arttext }}
											</span>
											<Tooltip
												:title="addon.arttext"
												:text="[addon.longdescription]"
											/>
										</h5>
									</li>
								</template>
							</ul>
						</div>
					</div>

					<!---********** Chekin **********-->
					<div v-if="accommodation.checkin.title" class="accommodation__check">
						<div class="accommodation__icon">
							<svg data-name="Lager 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path d="M23.59 16.08L19.86 4.94a6 6 0 00-7.6-3.78 6 6 0 00-2.82 2 6 6 0 00-5.66 3.48 6 6 0 00-.21 4.59 6.18 6.18 0 00.41.86L.1 20.37a1 1 0 00.48 1.33L3.29 23a1.07 1.07 0 00.43.09.91.91 0 00.51-.14l3.09-1.87a1 1 0 00.42-1.19l-.14-.39.4-.16a1 1 0 00.6-1.28l-.14-.39.39-.14a1 1 0 00.56-.53l.9-1.93.27-.07 1.64 4.89a.94.94 0 00.5.57 1 1 0 00.44.11 1.13 1.13 0 00.32 0L23 17.35a1 1 0 00.59-1.27zm-15.82-.29l-.94.34a1 1 0 00-.6 1.28l.14.39-.37.14a1 1 0 00-.6 1.28l.2.56-1.94 1.16-1.33-.62 3.07-6.53a6 6 0 001.26.83 5.89 5.89 0 001.44.46zm2.8-2.79a4 4 0 11-1.35-7.81A4 4 0 0113 7.83a3.82 3.82 0 01.16 2.06 3 3 0 01-1.61-1 2 2 0 00-1-2.45 2 2 0 00-1.7 3.62 2 2 0 00.85.19 2.08 2.08 0 00.68-.12 2 2 0 00.62-.36 4 4 0 001.88 1.07v.05A3.92 3.92 0 0110.57 13zm0-4.24a1 1 0 01-.56.51 1 1 0 01-.77 0 1 1 0 01.43-1.91.9.9 0 01.42.1 1 1 0 01.48 1.33zm3.25 9.6l-1.36-4.07a6 6 0 002.21-2.5 5.73 5.73 0 00.35-1A4.16 4.16 0 0017.32 9a.51.51 0 00-.18-.69.5.5 0 00-.68.18 3.07 3.07 0 01-1.27 1.15 5.94 5.94 0 00-3.43-5.86h-.08a4 4 0 011.21-.66A4 4 0 0118 5.58l3.42 10.19z"/></svg>
						</div>
						<div class="accommodation__time-wrapper">
							<div>
								<p>{{ accommodation.checkin.title }}</p>
								<div class="accommodation__time">
									{{ accommodation.checkin.time }}
								</div>
							</div>
							<div>
								<p>{{ accommodation.checkout.title }}</p>
								<div class="accommodation__time">
									{{ accommodation.checkout.time }}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="cell small-12 large-3">
				<!---********** Location **********-->
				<div class="accommodation__location">
					<div class="accommodation__map" v-if="!loading">
						<Googlemaps
							:centerPos="markerCenter"
							:markers="markers"
							:hasMultiMarkers="false"
						/>
					</div>
					<!--<div class="accommodation__adress">
            <div class="accommodation__icon">
              <svg data-name="Lager 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path class="cls-1" d="M19 4H5a3 3 0 00-3 3v10a3 3 0 003 3h14a3 3 0 003-3V7a3 3 0 00-3-3zm1 13a1 1 0 01-1 1H5a1 1 0 01-1-1V9.87l8 5.33 8-5.33zm0-9.54l-8 5.34-8-5.34V7a1 1 0 011-1h14a1 1 0 011 1z"/></svg>
            </div>
            <div class="accommodation__adress-wrapper">
              <div class="accommodation__title--small">{{accommodation.title}}</div>
              <a href="www.klappen.se" class="accommodation__url">www.klappen.se</a>
              <div class="accommodation__phone">0280-962 00</div>
            </div>
					</div>-->
				</div>
			</div>
		</div>

		<!---********** Related Accommodation **********-->
		<!-- <div
			v-if="
				!loading &&
				accommodation.similarAccommodations &&
				accommodation.similarAccommodations.length
			"
			class="grid-x grid-margin-y"
		>
			<div class="cell small-12">
				<div class="accommodation-slider-wrapper">
					<h3 class="accommodation__title">
						{{ $t('accommodation.relatedAccommodations') }}
					</h3>
					<Accommodationslider
						:perPage="1"
						:similarAccommodation="similarAcc"
						:similarAccommodationIDList="accommodation.similarAccommodations"
					/>
				</div>
			</div>
		</div> -->

		<!---********** Added to cat Modal **********-->
		<!-- <Modal
			v-if="showModalAdded"
			@close=";(showModalAdded = false), (showModal = false)"
		>
			<div class="accommodation__modal">
				<h4 class="accommodation__modal-added">
					{{ $t('accommodation.modalAddedTitle') }}
				</h4>
				<p>{{ $t('accommodation.modalAddedText') }}</p>
				<router-link :to="{ name: 'cart' }">
					<Button :full="true">{{ $t('accommodation.modalLinkOne') }}</Button>
				</router-link>
				<Button
					@click.native="showModalAdded = false"
					color="dark"
					:full="true"
				>
					{{ $t('accommodation.modalLinkTwo') }}
				</Button>
			</div>
		</Modal> -->

		<!---********** Datepicker Modal **********-->
		<Modal v-if="showModal" @close="closeDatepicker" @update="update">
			<div class="accommodation__modal">
				<h3>
					<svg data-name="Lager 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path d="M22 2H2a1 1 0 00-1 1v18a1 1 0 001 1h20a1 1 0 001-1V3a1 1 0 00-1-1zM6.75 20H3v-3h3.75zm0-4H3v-3h3.75zm0-4H3V9h3.75zm4.75 8H7.75v-3h3.75zm0-4H7.75v-3h3.75zm0-4H7.75V9h3.75zm4.75 8H12.5v-3h3.75zm0-4H12.5v-3h3.75zm0-4H12.5V9h3.75zM21 20h-3.75v-3H21zm0-4h-3.75v-3H21zm0-4h-3.75V9H21zm0-5H3V4h18z"/></svg>
					<span>{{ $t('accommodation.selectAmongAvailableDates') }}</span>
				</h3>

				<!-- active dates -->
				<div class="accommodation__current-dates">
					<h5>
						{{ $t('accommodation.selectedDates') }}: {{ datePickerStartDate }} -
						{{ datePickerEndDate }}
					</h5>
				</div>

				<DatePicker
					:selectForward="false"
					:format="'YYYY-MM-DD'"
					:allowedDates="allowedStartDates"
					:minNight="minNight"
					:maxNight="maxNight"
					:maxDate="getMaxDate"
					:startDate="datePickerStartDate"
					:endDate="datePickerEndDate"
					:monthList="$t('datepicker.monthList')"
					:placeholder="$t('datepicker.selectDateRange')"
					:startdateHelptext="$t('datepicker.selectArrivalDate')"
					:enddateHelptext="$t('datepicker.selectDepartureDate')"
					@update="update"
					@reset="reset"
				/>
				<div class="datepicker-alert">
					<AlertMessage :show="false" :blue="true" :fat="true">
						{{ $t('search.datepickerAlertText') }}
					</AlertMessage>
				</div>

				<InputComponent
					v-model="campaignCode.value"
					:placeholderValue="$t('search.placeholderAddCampaigncode')"
					:hasError="campaignCode.error"
					:errorMessage="campaignCode.message"
					:lightTheme="true"
					:accepted="campaignCode.accepted"
				/>

				<div class="accommodation__current-num-people">
					<h4 class="accommodation__number-title">
						{{ $t('accommodation.numberOfPeople') }}: {{ totalPeaple() }}
					</h4>
				</div>

				<!-- People slecter -->
				<div class="accommodation__number-people">
					<NumberComponent
						:label="$t('search.numberLabelChildren')"
						:underlabel="$t('search.numberLabelChildrenYears')"
						:initalValue="numberOfPersons.amountChildren"
						:disablePlus="
							accommodation.sleepingArrangementBeds <= totalPeaple()
						"
						@number-input="
							val => {
								this.numberOfPersons.amountChildren = val
							}
						"
					/>
					<NumberComponent
						:label="$t('search.numberLabelYouth')"
						:underlabel="$t('search.numberLabelYouthYears')"
						:initalValue="numberOfPersons.amountYouths"
						:disablePlus="
							accommodation.sleepingArrangementBeds <= totalPeaple()
						"
						@number-input="
							val => {
								this.numberOfPersons.amountYouths = val
							}
						"
					/>
					<NumberComponent
						:label="$t('search.numberLabelAdult')"
						:underlabel="$t('search.numberLabelAdultYears')"
						:initalValue="numberOfPersons.amountAdults"
						:disablePlus="
							accommodation.sleepingArrangementBeds <= totalPeaple()
						"
						@number-input="
							val => {
								this.numberOfPersons.amountAdults = val
							}
						"
					/>
				</div>

				<!-- Error message -->
				<p v-if="erroMessage.show === true" class="error-message colorRed">
					{{ errorMessage.text }}
				</p>

				<Button
					@click.native="
						validateDateAndPeaple() == false
							? confirmGetAccomodation({
									poollid: accommodation.poollid,
									unitlid: accommodation.unitlid,
									startDate: datePickerStartDate,
									endDate: datePickerEndDate,
									campaignCode:
										campaignCode.value !== ''
											? campaignCode.value
											: accommodation.promotion !== null
											? accommodation.promotion.promotioncode
											: undefined,
									...numberOfPersons // eslint-disable-next-line
							})
							: () => {}
					"
					:loading="loading"
					:disabled="validateDateAndPeaple()"
				>
					{{ $t('accommodation.done') }}
				</Button>
			</div>
		</Modal>

		<!--******** Book same period *********-->
		<Modal
			v-if="showPeriodInfoModal === true"
			@close="showPeriodInfoModal = false"
		>
			<h4>{{ $t('accommodation.modalPeriodTitle') }}</h4>
			1
			<br />
			<p>{{ $t('accommodation.modalPeriodBody') }}</p>
		</Modal>

		<!--******** AccNotAwalible *********-->
		<Modal
			v-if="shwoErrorAccNotAwalible === true"
			@close="shwoErrorAccNotAwalible = false"
		>
			<h4>{{ $t('errorText.notAvailable') }}</h4>
			<br />
			<p>{{ $t('errorText.dateNotAvailable') }}</p>
			<br />
			<Button @click.native="shwoErrorAccNotAwalible = false">
				{{ $t('accommodation.confirm') }}
			</Button>
		</Modal>
	</section>
</template>

<script>
import store from '../vueX/store'
import mixin from '../mixin'
import moment from 'moment'

import Button from '../components/atoms/Button.vue'
import ImageSlider from '../components/molecules/ImageSlider.vue'
import DatePicker from '../components/molecules/DatePicker'
import Googlemaps from '../components/molecules/GoogleMaps.vue'
import Accommodationinfo from '../components/molecules/AccommodationInfo.vue'
// import Accommodationslider from '../components/molecules/AccommodationSlider.vue'
import NumberComponent from '../components/atoms/NumberComponent'
import InputComponent from '../components/atoms/InputComponent'
import Modal from '../components/molecules/Modal'
import Tooltip from '../components/molecules/Tooltip'
import AlertMessage from '../components/atoms/AlertMessage'

export default {
	name: 'Accommodation',
	store,
	mixins: [mixin],
	data() {
		return {
			loading: true,
			prevRoute: null,
			accommodation: {},
			accommodationAddon: [],
			markers: [],
			markerCenter: {},
			similarAcc: [],
			showModal: false,
			showModalAdded: false,
			showPeriodInfoModal: false,
			shwoErrorAccNotAwalible: false,
			//Datepicker
			datePickerStartDate: '',
			datePickerEndDate: '',
			selectedStartDate: '',
			selectedEndDate: '',
			minNight: 0,
			maxNight: 0,
			getMaxDate: '',
			allowedStartDates: [],
			isDiscount: false,

			numberOfPersons: {
				amountAdults: 2,
				amountChildren: 0,
				amountYouths: 0
			},
			erroMessage: {
				show: false,
				text: null
			},
			campaignCode: {
				value: '',
				message: '',
				show: false,
				error: false,
				accepted: false
			}
		}
	},
	components: {
		ImageSlider,
		DatePicker,
		Button,
		NumberComponent,
		InputComponent,
		Googlemaps,
		Accommodationinfo,
		// Accommodationslider,
		Modal,
		Tooltip,
		AlertMessage
	},
	beforeRouteEnter(to, from, next) {
		next(vm => {
			vm.prevRoute = from
		})
	},
	beforeRouteUpdate(to, from, next) {
		const params = this.querysAndParamsToGetAccomodation(to)

		if (to.params.id !== from.params.id) {
			// Get Accometation and price (with dates)
			if (params.startDate && params.endDate) {
				this.getAccommodationWithDate({
					...params
				})
			} else {
				// Get Accometation without dates (No price)
				this.getAccommodation(params)
			}
		}
		next()
	},
	methods: {
		convertPrice(price) {
			return new Intl.NumberFormat(this.$t('currency')).format(price)
		},

		// getDiscountLabel() {
		// 	return '-10%' // ToDo: some calculation to get percentage
		// },

		update(data) {
			//Sätter valt start och slutdatum
			this.datePickerStartDate = data.start
			this.datePickerEndDate = data.end
			//hämtar all tillgänliga datum för det valda datumet
			const getSelectedDate = this.accommodation.availableDates.filter(item => {
				if (item.date == data.start) {
					return item.date
				}
			})

			if (getSelectedDate != '') {
				//kollar om boendet har inte har ett max antal för bokningsbara dagar
				if (getSelectedDate[0].unlimitedDays == true) {
					this.allowedStartDates = []
					this.getMaxDate = getSelectedDate[0].periodEndDate
				} else {
					this.allowedStartDates = getSelectedDate[0].endDates
				}
			}
		},

		closeDatepicker() {
			this.showModal = false
			this.datePickerStartDate = this.selectedStartDate
			this.datePickerEndDate = this.selectedEndDate
		},

		//återställer all datum till ursprungsdatum
		reset() {
			this.datePickerStartDate = ''
			this.datePickerEndDate = ''

			const availableDates = this.accommodation.availableDates.map(
				item => item.date
			)
			const endDates = this.accommodation.availableDates
				.map(item => item.endDates)
				.flat()
				.sort()

			this.getMaxDate = endDates[endDates.length - 1]
			this.maxNight = 0
			this.minNight = 0
			this.allowedStartDates = availableDates
		},

		totalPeaple() {
			return (
				parseInt(this.numberOfPersons.amountAdults) +
				parseInt(this.numberOfPersons.amountYouths) +
				parseInt(this.numberOfPersons.amountChildren)
			)
		},

		validateDateAndPeaple() {
			return !(
				this.datePickerStartDate &&
				this.datePickerEndDate &&
				this.numberOfPersons.amountAdults > 0
			)
		},

		confirmGetAccomodation(params) {
			this.setRouteQuerys({
				s: params.startDate,
				e: params.endDate,
				a: params.amountAdults ? parseInt(params.amountAdults) : 0,
				c: params.amountChildren ? parseInt(params.amountChildren) : 0,
				y: params.amountYouths ? parseInt(params.amountYouths) : 0,
				cc: params.campaignCode //this.$route.query.cc ? this.$route.query.cc : undefined
			})

			this.getAccommodationWithDate(params)
		},

		addToCart() {
			if (
				store.state.cart.accommodation[0] &&
				moment(store.state.cart.accommodation[0].arrdate).isSame(
					this.selectedStartDate,
					'date'
				) === false &&
				moment(store.state.cart.accommodation[0].depdate).isSame(
					this.selectedEndDate,
					'date'
				) === false
			) {
				this.showPeriodInfoModal = true
			} else {
				store
					.dispatch('PUT_ACCOMMODATION_IN_CART', {
						customerlid: null,
						allowcancellation: true,
						allowmodify: true,
						poollid: Number(this.accommodation.poollid),
						unitlid: Number(this.accommodation.unitlid),
						objectId: this.accommodation.id,
						sleepingArrangementBeds: this.accommodation.sleepingArrangementBeds,
						title: this.accommodation.title,
						arrdate: this.selectedStartDate,
						depdate: this.selectedEndDate,
						pricecode: this.accommodation.pricecode,
						currency: 'SEK',
						promotionlid: this.accommodation.promotionlid,
						price: this.accommodation.price,
						ordinaryprice: this.accommodation.ordinaryprice,
						addons: this.accommodationAddon,
						noofchild2: this.numberOfPersons.amountChildren,
						noofchild3: this.numberOfPersons.amountYouths,
						noofchild5: this.numberOfPersons.amountAdults,
						original_noofchild2: this.numberOfPersons.amountChildren,
						original_noofchild3: this.numberOfPersons.amountYouths,
						original_noofchild5: this.numberOfPersons.amountAdults,
						image: this.mapImages()[0],
						type: this.accommodation.type,
						plusaccommodation: this.accommodation.plusaccommodation,
						departurecleaning: this.accommodation.departurecleaning,
						breakfastincluded: this.accommodation.breakfastincluded,
						linensetincluded: this.accommodation.linensetincluded,
						qty: 1,
						resvid: 0,
						headlid: 0,
						detlid: 0,
						lang: '',
						campaignCode: this.accommodation.promotion.promotioncode,
						promotion:
							this.accommodation.promotion.promotioncode != null
								? this.accommodation.promotion
								: undefined
					})
					.then(() => {
						this.loading = false
						store.dispatch(
							'GTM_PUSH_ACCOMMODATION_ADD_TO_CART',
							this.accommodation
						)
						// this.showModalAdded = true
						this.$router.push({ name: 'addtocart' })
					})
			}
		},

		removeFromCart() {
			store.dispatch('REMOVE_ACCOMMODATION_FROM_CART', this.accommodation)
		},

		getMapMarkers() {
			this.markers = [
				{
					position: {
						lat: this.accommodation.location.lat
							? this.accommodation.location.lat
							: 61.035245,
						lng: this.accommodation.location.lng
							? this.accommodation.location.lng
							: 13.3469
					}
				}
			]
			this.markerCenter = {
				lat: this.accommodation.location.lat,
				lng: this.accommodation.location.lng
			}
		},

		mapImages() {
			if (this.accommodation && this.accommodation.images) {
				return this.accommodation.images.map(each => each.img)
			}
			return []
		},

		fromWhat() {
			if (this.prevRoute && this.prevRoute.name == 'search') {
				return true
			}
			return false
		},

		goBack() {
			this.$router.go(-1)
		},

		setDescription() {
			let discription = this.accommodation.areaDescription

			if (discription.length > 150) {
				discription = discription.slice(0, 150)
			}

			document
				.querySelector('meta[name="description"]')
				.setAttribute('content', discription)
		},

		setRouteQuerys(querys) {
			// Dont update querys if thay are the same
			let newURL = false
			for (const each in querys) {
				if (this.$route.query.hasOwnProperty(each)) {
					if (this.$route.query[each] != querys[each]) {
						newURL = true
						break
					}
				} else {
					newURL = true
					break
				}
			}

			if (newURL) {
				this.$router.replace({ query: { ...querys } })
			}
		},

		getAccommodationWithDate(params) {
			this.loading = true

			store
				.dispatch('GET_ACCOMMODATIONS_WITH_DATES', { ...params })
				.then(success => {
					if (success && Array.isArray(success) && success.length) {
						const newAccommodation = success[0] ? success[0] : success
						this.accommodation = newAccommodation
						this.isDiscount =
							params.campaignCode !== undefined &&
							newAccommodation.ordinaryprice !== undefined &&
							newAccommodation.ordinaryprice !== '' &&
							newAccommodation.ordinaryprice !== -1 &&
							newAccommodation.price !== ''

						store.dispatch('GTM_PUSH_ACCOMMODATION_DETAILS', newAccommodation)

						this.allowedStartDates = [params.startDate, params.endDate]
						this.getMapMarkers()
						this.selectedStartDate = params.startDate
						this.selectedEndDate = params.endDate
						this.datePickerStartDate = params.startDate
						this.datePickerEndDate = params.endDate
						this.numberOfPersons = {
							amountAdults: params.amountAdults
								? parseInt(params.amountAdults)
								: 0,
							amountChildren: params.amountChildren
								? parseInt(params.amountChildren)
								: 0,
							amountYouths: params.amountYouths
								? parseInt(params.amountYouths)
								: 0
						}

						const changedAddons = newAccommodation.addons.map(obj =>
							obj.mandatory_web === false ? { ...obj, qty: 0 } : obj
						)

						this.accommodationAddon = changedAddons

						this.loading = false
						this.showModal = false
						this.setDescription()
					} else if (
						success.errorCode !== undefined &&
						success.errorCode === 0 &&
						this.campaignCode.value
					) {
						this.campaignCode.message = this.$t('search.errorCampaignCode')
						this.campaignCode.error = true
						this.showModal = true
						this.loading = false
						this.$router.replace({})
					} else {
						this.errorHandelingNotAwalible()
						this.$router.replace({})
					}
				})
				.catch(error => {
					window.console.error(error)
					this.errorHandelingNotAwalible()
					this.$router.replace({})
				})
		},

		getAccommodation() {
			this.loading = true

			const mapParams = {
				poollid: this.$route.params.id.split('_')[1],
				unitlid: this.$route.params.id.split('_')[2]
			}

			store.dispatch('GET_ACCOMMODATION', { ...mapParams }).then(success => {
				if (success) {
					this.accommodation = success
					store.dispatch('GTM_PUSH_ACCOMMODATION_DETAILS', success)
					this.allowedStartDates = success.availableDates
						? success.availableDates.map(each => each.date)
						: []
					this.getMapMarkers()
					this.setDescription()
				}
				this.loading = false
			})
		},

		errorHandelingNotAwalible() {
			window.console.warn('Accommodation not available selected dates')
			this.shwoErrorAccNotAwalible = true
			// get acc whitout date
			this.getAccommodation()

			//show error modal
		},

		querysAndParamsToGetAccomodation(to) {
			const routeParams = to ? to.params : this.$route.params
			const routeQuerys = to ? to.query : this.$route.query

			const mapParams = {
				poollid: routeParams.id.split('_')[1],
				unitlid: routeParams.id.split('_')[2]
			}

			const mapQuerys = {
				startDate: routeQuerys.s,
				endDate: routeQuerys.e,
				amountAdults: routeQuerys.a
					? routeQuerys.a
					: this.numberOfPersons.amountAdults,
				amountChildren: routeQuerys.c
					? routeQuerys.c
					: this.numberOfPersons.amountChildren,
				amountYouths: routeQuerys.y
					? routeQuerys.y
					: this.numberOfPersons.amountYouths
			}

			if (routeQuerys.cc !== undefined) {
				mapQuerys.campaignCode = routeQuerys.cc
			}

			return { ...mapParams, ...mapQuerys }
		},

		isAccomodationInCart() {
			if (store.state.cart.accommodation.length) {
				for (let i = 0; i < store.state.cart.accommodation.length; i++) {
					const each = store.state.cart.accommodation[i]
					if (
						each.poollid == this.accommodation.poollid &&
						each.unitlid == this.accommodation.unitlid &&
						each.arrdate == this.selectedStartDate &&
						each.depdate == this.selectedEndDate
					) {
						return true
					}
				}
			}
			return false
		},

		showDatepickerModal() {
			if (!this.isAccomodationInCart()) {
				this.showModal = true
			}
		},

		mandatoryAddons() {
			const mandatoryAddonsList = []

			//PLUS
			if (this.accommodation.plusaccommodation) {
				mandatoryAddonsList.push(this.$t('accommodation.plusLabel'))
			}

			//Skipass
			this.accommodation.addons.forEach(function (addon) {
				if (addon.mandatory_web === true && addon.resvtypelid === 1001) {
					mandatoryAddonsList.push(addon.qty + ' ' + addon.arttext)
				}
			})

			//Breakfast
			if (this.accommodation.breakfastincluded) {
				mandatoryAddonsList.push(
					this.totalPeople() + ' ' + this.$t('accommodation.breakfast') + '*'
				)
			}

			//Linen
			if (this.accommodation.linensetincluded) {
				mandatoryAddonsList.push(
					this.totalPeople() + ' ' + this.$t('cart.linenSet')
				)
			}

			//Departure cleaning
			if (this.accommodation.departurecleaning) {
				mandatoryAddonsList.push(this.$t('cart.departureCleaning'))
			}

			return mandatoryAddonsList
		},

		totalPeople() {
			return (
				parseInt(this.numberOfPersons.amountAdults) +
				parseInt(this.numberOfPersons.amountYouths) +
				parseInt(this.numberOfPersons.amountChildren)
			)
		}
	},
	created: function () {
		const params = this.querysAndParamsToGetAccomodation()

		// Get Accometation and price (with dates)
		if (params.startDate && params.endDate) {
			this.getAccommodationWithDate({
				...params
			})
		} else {
			// Get Accometation without dates (No price)
			this.getAccommodation(params)
		}
	}
}
</script>

<style scoped lang="scss">
%border {
	padding: 35px 0;
	border-bottom: 1px solid #cccccc7d;
}

%pricerwrapper {
	display: flex;
	align-items: center;
	justify-content: space-between;
	position: fixed;
	width: 100%;
	bottom: 0;
	left: 0;
	z-index: 9;
	@include for-tablet-portrait-up {
		right: 0;
		bottom: 0;
		height: 50px;
		position: relative;
		width: auto;
	}
}

.googlemaps {
	width: 100%;
	height: 360px;
	position: relative;
}

.error-message {
	margin-top: 20px;
	margin-bottom: 20px;
}

.accommodation {
	@include for-phone-only {
		padding: 0 0 60px 0;
	}

	@include for-tablet-portrait-up {
		margin-top: 60px;
	}
	&__back {
		border: none;
		background-color: white;
		color: #7f8ba1;
		font-size: 16px;
		padding-left: 48px;
		padding: 20px;
		span:last-child {
			padding-left: 20px;
		}
		&:hover {
			cursor: pointer;
			text-decoration: underline;
		}
	}
	&__imageslider {
		width: 100%;
		height: 360px;
		@include for-tablet-portrait-up {
			position: relative;
			height: 480px;
		}
	}

	&__current-dates {
		button {
			margin-top: 15px;
		}

		+ * {
			margin-top: 20px;
		}

		+ .accommodation__current-num-people {
			margin-top: 40px;
		}
	}

	&__head {
		position: relative;
		display: flex;
		justify-content: space-between;
	}

	&__dates {
		padding: 20px;
		position: relative;
		background: #fff;
		border: none;
		width: 100%;
		text-align: left;

		&--cursor {
			cursor: pointer;
		}

		.selected-people {
			> * {
				text-transform: lowercase;
				&:before {
					content: ' +';
					display: inline;
				}

				&:first-child:before {
					display: none;
				}
			}
			&__breakfast:before {
				display: none;
			}
		}

		&__title {
			display: flex;
			@include for-tablet-portrait-up {
				justify-content: space-between;
			}
			span {
				margin-left: auto;
				@include for-tablet-portrait-up {
					margin-left: 0;
				}
			}

			h6 {
				font-weight: 600;
			}

			+ p {
				margin-top: 10px;
			}

			svg {
				width: 20px;
				height: 20px;

				&.icon-calendar {
					fill: $colorPrimary6;
					margin-right: 14px;
					@include for-tablet-portrait-up {
						margin-right: 0;
					}
				}
				&.icon-pen {
					fill: $colorSecondary;
				}
			}
		}

		&__labels {
			margin: 0 0 -5px 0;

			&-headline {
				display: block;
				text-transform: uppercase;
				color: $colorGrey8;
				font-size: 10px;
				font-weight: 500;
				margin: 10px 0 5px 0;
			}

			li {
				display: inline-block;
				background: $colorPrimary1;
				margin: 0 5px 5px 0;
				padding: 5px 8px;
				border-radius: 4px;
				font-size: 13px;
				line-height: 15px;
				color: $colorPrimary8;
			}
		}

		&--disabled {
			cursor: default;

			.accommodation__dates__title {
				justify-content: flex-start;

				h6 {
					margin-left: 15px;
				}
			}
		}
	}

	&__sticky {
		position: fixed;
		bottom: 0;
		width: 100%;
		z-index: 10;
		display: flex;
		padding: 10px 20px;
		box-sizing: border-box;
		justify-content: space-between;
		background: #7f8ba1;
		@include for-tablet-portrait-up {
			display: none;
		}
	}
	&__stickyprice {
		color: $colorWhite;
	}
	&__stickydate {
		color: $colorPrimary2;
	}

	&__book {
		position: relative;
		background-color: $colorWhite;
		box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.1);
		transform: translate(0, -48px);
		z-index: 10;
		margin-bottom: -20px;

		@include for-tablet-portrait-up {
			transform: translate(-30px, -50%);
			max-width: 320px;
			position: absolute;
			right: 0;
			top: 0;
			margin-bottom: 0;
			/*transform: translate(60px, 0);
			right: 0;
			position: relative;
			width: auto;
			height: 100%;*/
		}
		/*@media only screen and (max-width: 900px) {
			position: fixed;
			left: 0;
			right: 0;
			bottom: 0;
			transform: none;
			z-index: 10;
			box-shadow: 0 3px 6px 2px #ccc;
		}*/
		&__actions {
			background: #62d8ff;
			display: flex;
			justify-content: space-between;
			align-items: center;
		}

		&--discount {
			.accommodation {
				&__book {
					&__actions {
						background: #e15b5b;
					}
				}

				&__price {
					// display: flex;
					// align-items: center;
					// justify-content: flex-start;
					// flex-direction: column;

					&__full {
						display: flex;
						font-size: 11px;
						font-weight: 300;
						letter-spacing: 0;
						line-height: 12px;
					}
				}

				&__discountLabelWrapper {
					display: flex;
					align-items: center;
					justify-content: flex-start;
					flex-direction: row;
					line-height: 22px;
				}

				&__discontLabel {
					display: flex;
					align-items: center;
					margin-left: 8px;
					padding: 0 4px;
					border-radius: 3px;
					background-color: #b9003c;

					&__value {
						font-size: 14px;
						font-weight: 500;
						line-height: normal;
					}
				}
			}
		}
	}
	&__availability {
		button {
			width: 100%;
		}
		@include for-tablet-portrait-up {
			button {
				width: auto;
			}
			right: 0px;
		}
	}
	&__price {
		min-width: 165px;
		color: #ffffff;
		font-family: Saira;
		font-size: 20px;
		font-weight: 600;
		text-shadow: 1px 1px 0px rgba(0, 106, 141, 0.5);
		line-height: 32px;
		padding-left: 20px;
	}
	&__type {
		color: #697791;
		font-size: 13px;
		line-height: 21px;
	}
	&__title {
		color: #434449;
		font-family: Saira;
		font-size: 28px;
		line-height: 40px;
		margin: 0 0 0 0;
		@include for-tablet-portrait-up {
			margin: 10px 0 20px 0;
		}
		&--small {
			color: #4a4a4a;
			font-family: Saira;
			font-size: 14px;
			font-weight: bold;
			line-height: 24px;
			margin-top: 5px;
		}
	}
	&__description,
	&__more {
		white-space: pre-line;
		font-size: 16px;
		font-family: Saira;
		line-height: 24px;
		@include for-tablet-portrait-up {
			font-size: 18px;
			line-height: 28px;
		}
	}
	&__more {
		color: #7f8ba1;
		background: none;
		border: 0;
		text-decoration: underline;
		cursor: pointer;
		padding: 0;
	}
	&__location {
		@include for-tablet-portrait-up {
			margin-top: 60px;
			position: relative;
		}
	}
	&__adress-wrapper {
		color: #4a4a4a;
		font-family: Saira;
		font-size: 14px;
		line-height: 24px;
	}
	&__adress {
		padding: 40px;
		display: flex;
	}

	&__info {
		position: relative;
		padding: 20px 10px;
		@include for-phone-only {
			padding: 20px;
		}
		@include for-tablet-portrait-up {
			padding: 40px 100px;
		}
	}
	&__label {
		color: #000000;
		font-family: Saira;
		font-size: 20px;
		line-height: 32px;
		padding-bottom: 20px;
	}
	&__utilities {
		display: flex;
		flex-wrap: wrap;
		padding-top: 35px;
		&--item {
			margin-right: 40px;
			padding: 10px 0;
			display: flex;
			align-items: flex-end;
			svg {
				width: 24px;
				height: 24px;
				fill: $colorPrimary6;
				padding-right: 10px;
			}
		}
	}
	&__sleeping-arrangements {
		@extend %border;
		margin-top: 0;
	}
	&__facilities {
		@extend %border;
		display: flex;
		&-wrapper {
			width: 100%;
		}
	}
	&__addon-description {
		&:after {
			content: '';
			background: #ccc;
		}
		@extend %border;
		display: flex;
		&-wrapper {
			width: 100%;
			h5 {
				display: flex;
				align-content: center;
				align-items: center;
			}
			ul {
				list-style-type: none;
				// padding-left: 20px;

				/*@include for-phone-only {
					margin-left: -54px;
				}*/

				.bull {
					margin-right: 20px;
				}

				li {
					margin-top: 7px;
					// white-space: pre-line;
					&:first-child {
						margin-top: 0;
					}

					h4 + * {
						margin-top: 5px;
					}

					p {
						font-size: 12px;
					}
				}
			}
		}
	}
	&__list {
		display: flex;
		flex-flow: wrap column;

		@include for-tablet-portrait-up {
			flex-flow: wrap row;
		}

		li {
			@include for-tablet-portrait-up {
				flex-basis: 200px;
			}

			//height: 20px;
			color: #4a4a4a;
			font-family: Saira;
			font-size: 14px;
			line-height: 16px;
			padding-right: 20px;
			padding-bottom: 10px;
		}
		svg {
			margin-right: 8px;
			margin-top: 3px;
			display: inline-block;
			width: 10px;
			height: 10px;
		}
	}
	&__addons-list {
		list-style-type: none;
	}
	&__check {
		@extend %border;
		border: 0;
		display: flex;
	}
	&__time-wrapper {
		display: flex;
		flex-wrap: wrap;
		p {
			font-size: 16px;
		}
		div:first-child {
			padding-right: 40px;
		}
	}
	&__time {
		font-family: Saira;
		font-size: 18px;
		line-height: 26px;
	}
	&__district {
		@extend %border;
		padding: 35px 0;
		display: flex;
	}
	&__add-on {
		padding: 35px 0;
		display: flex;
	}
	&__icon {
		padding-right: 20px;
		@include for-tablet-portrait-up {
			padding-right: 30px;
		}
		svg {
			width: 24px;
			height: 24px;
			fill: $colorPrimary6;
			margin-top: 5px;
		}
	}
	&__accommodationslider {
		padding: 40px 0px 40px 100px;
	}

	@media only screen and (max-width: 40em) {
		.accommodation-slider-wrapper {
			padding: 0 20px;
		}
	}
	&__modal-added {
		margin: 10px 0;
	}
	&__modal {
		button {
			margin: 10px 0;
		}
		svg {
			height: 30px;
			width: 33px;
			fill: #546482;
			margin-right: 14px;
		}
		h3 {
			display: flex;
			align-items: center;
			margin-bottom: 36px;
		}
	}
	&__number-title {
		margin: 20px 0;
	}
	&__number-people {
		display: flex;
		flex-flow: row wrap;

		> div {
			flex-grow: calc(100% / 4 - 20px);
			margin: 0px 20px 20px 0px;
		}
	}
}

.input__container::v-deep {
	margin: 20px 0;

	.input__error {
		position: relative;
	}
}
</style>
